import { z } from 'zod';
import {
  ASSIGNMENT_STRATEGIES,
  CRM_MODULES,
  WORKFLOW_ACTION_TYPES,
  WORKFLOW_TRIGGER_TYPES,
  type ApprovalStatus,
  type CrmModule,
  type WorkflowActionType,
  type WorkflowTriggerType,
} from '../enums.js';
import {
  optionalText,
  paginationQuerySchema,
  toPatchSchema,
  uuidSchema,
} from '../schemas/common.js';
import { conditionTreeSchema } from './conditions.js';

export const workflowActionSchema = z.object({
  type: z.enum(WORKFLOW_ACTION_TYPES),
  config: z.record(z.string(), z.unknown()).default({}),
  position: z.coerce.number().int().min(0).max(50).default(0),
  /** 0 runs inline with the triggering write. */
  delayMinutes: z.coerce.number().int().min(0).max(43_200).default(0),
});
export type WorkflowActionInput = z.infer<typeof workflowActionSchema>;

export const createWorkflowSchema = z
  .object({
    name: z.string().trim().min(1, 'Required').max(120),
    description: optionalText(1000),
    module: z.enum(CRM_MODULES),
    triggerType: z.enum(WORKFLOW_TRIGGER_TYPES),
    /** Which fields arm a FIELD_CHANGED rule. */
    triggerFields: z.array(z.string().min(1).max(120)).max(20).default([]),
    conditions: conditionTreeSchema.default({}),
    actions: z.array(workflowActionSchema).min(1, 'Add at least one action').max(20),
    isActive: z.coerce.boolean().default(true),
    /** Guards runaway loops when an action re-triggers the same workflow. */
    maxExecutionsPerRecord: z.coerce.number().int().min(1).max(100).default(1),
  })
  .refine(
    (value) => value.triggerType !== 'FIELD_CHANGED' || value.triggerFields.length > 0,
    // Without a field list, "field changed" means "any change", which is what
    // RECORD_UPDATED already is.
    { message: 'Choose at least one field to watch', path: ['triggerFields'] },
  );
export type CreateWorkflowInput = z.infer<typeof createWorkflowSchema>;

export const updateWorkflowSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  description: optionalText(1000),
  conditions: conditionTreeSchema.optional(),
  actions: z.array(workflowActionSchema).min(1).max(20).optional(),
  triggerFields: z.array(z.string().min(1).max(120)).max(20).optional(),
  isActive: z.coerce.boolean().optional(),
  maxExecutionsPerRecord: z.coerce.number().int().min(1).max(100).optional(),
});
export type UpdateWorkflowInput = z.infer<typeof updateWorkflowSchema>;

export const listWorkflowsQuerySchema = paginationQuerySchema.extend({
  module: z.enum(CRM_MODULES).optional(),
  isActive: z.stringbool().optional(),
});
export type ListWorkflowsQuery = z.infer<typeof listWorkflowsQuerySchema>;

export interface WorkflowActionSummary {
  id: string;
  type: WorkflowActionType;
  config: Record<string, unknown>;
  position: number;
  delayMinutes: number;
}

export interface WorkflowSummary {
  id: string;
  name: string;
  description: string | null;
  module: CrmModule;
  triggerType: WorkflowTriggerType;
  triggerFields: string[];
  conditions: unknown;
  /** Plain-English rendering of the condition tree, for the list view. */
  conditionSummary: string;
  isActive: boolean;
  maxExecutionsPerRecord: number;
  actions: WorkflowActionSummary[];
  /** Rolling counters so an admin can see whether a rule actually fires. */
  stats: { total: number; succeeded: number; failed: number; skipped: number };
  createdAt: string;
  updatedAt: string;
}

export interface WorkflowExecutionSummary {
  id: string;
  workflowId: string;
  recordType: string;
  recordId: string;
  status: 'SUCCESS' | 'FAILED' | 'SKIPPED';
  error: string | null;
  actionResults: { type: string; status: string; detail?: string }[];
  durationMs: number | null;
  createdAt: string;
}

// --- Assignment rules ------------------------------------------------------

export const createAssignmentRuleSchema = z.object({
  name: z.string().trim().min(1, 'Required').max(120),
  module: z.enum(CRM_MODULES).default('LEAD'),
  strategy: z.enum(ASSIGNMENT_STRATEGIES).default('ROUND_ROBIN'),
  conditions: conditionTreeSchema.default({}),
  isActive: z.coerce.boolean().default(true),
  position: z.coerce.number().int().min(0).max(100).default(0),
  targets: z
    .array(
      z.object({ userId: uuidSchema, weight: z.coerce.number().int().min(1).max(100).default(1) }),
    )
    .min(1, 'Choose at least one person to assign to')
    .max(50),
});
export type CreateAssignmentRuleInput = z.infer<typeof createAssignmentRuleSchema>;

export const updateAssignmentRuleSchema = toPatchSchema(createAssignmentRuleSchema);
export type UpdateAssignmentRuleInput = z.infer<typeof updateAssignmentRuleSchema>;

export interface AssignmentRuleSummary {
  id: string;
  name: string;
  module: CrmModule;
  strategy: (typeof ASSIGNMENT_STRATEGIES)[number];
  conditions: unknown;
  conditionSummary: string;
  isActive: boolean;
  position: number;
  targets: { userId: string; name: string; weight: number }[];
  createdAt: string;
}

// --- Approvals -------------------------------------------------------------

export const createApprovalSchema = z.object({
  entityType: z.enum(['DEAL', 'QUOTE']),
  entityId: uuidSchema,
  reason: z.string().trim().min(1, 'Say why this needs approval').max(500),
  requestedChanges: z.record(z.string(), z.unknown()).default({}),
  approverId: uuidSchema.optional(),
});
export type CreateApprovalInput = z.infer<typeof createApprovalSchema>;

export const decideApprovalSchema = z.object({
  decision: z.enum(['APPROVED', 'REJECTED']),
  comment: optionalText(500),
});
export type DecideApprovalInput = z.infer<typeof decideApprovalSchema>;

export const listApprovalsQuerySchema = paginationQuerySchema.extend({
  status: z.enum(['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED']).optional(),
  /** Only the ones waiting on the caller. */
  mine: z.stringbool().optional(),
});
export type ListApprovalsQuery = z.infer<typeof listApprovalsQuerySchema>;

export interface ApprovalSummary {
  id: string;
  entityType: string;
  entityId: string;
  entityLabel: string | null;
  reason: string;
  status: ApprovalStatus;
  requestedChanges: Record<string, unknown>;
  decisionComment: string | null;
  decidedAt: string | null;
  requestedBy: { id: string; name: string } | null;
  approver: { id: string; name: string } | null;
  createdAt: string;
}
